import React, { useState, useEffect } from 'react';
import {
  useFacturasCompra,
  useCrearFacturaCompra,
  useEditarFacturaCompra,
  useAnularFactura,
  useFacturaCompra,
} from '../hooks/useFacturasCompra';
import WizardFacturaCompraNew from '../components/WizardFacturaCompraNew';
import DetalleFacturaCompra from '../components/DetalleFacturaCompra';
import type {
  WizardFacturaData,
  FiltrosFacturaCompra,
  FacturaCompra,
} from '../types/facturas';
import {
  FileText,
  Plus,
  Search,
  Eye,
  XCircle,
  Filter,
  Download,
  Calendar,
  Edit,
} from 'lucide-react';
import { formatearRut } from '../schemas/facturaSchema';

const formatearMoneda = (cantidad: number): string => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cantidad);
};

const formatearFecha = (fecha: string): string => {
  return new Date(fecha).toLocaleDateString('es-CL');
};

const FacturasCompra: React.FC = () => {
  const [showWizard, setShowWizard] = useState(false);
  const [showDetalle, setShowDetalle] = useState(false);
  const [facturaSeleccionada, setFacturaSeleccionada] = useState<number | null>(null);
  const [facturaParaEditar, setFacturaParaEditar] = useState<FacturaCompra | null>(null);
  const [filtros, setFiltros] = useState<FiltrosFacturaCompra>({
    limit: 50,
    offset: 0,
  });
  const [showFiltros, setShowFiltros] = useState(false);

  // Hooks
  const { data: facturasData, isLoading, error } = useFacturasCompra(filtros);
  const crearFacturaMutation = useCrearFacturaCompra();
  const editarFacturaMutation = useEditarFacturaCompra();
  const anularFacturaMutation = useAnularFactura();

  // Cuando se va a editar, se carga la factura completa (con detalles).
  // El wizard NO se abre hasta que este fetch haya completado para garantizar
  // que initialData siempre lleve los detalles con los precios correctos.
  const { data: facturaDetalleResponse, isLoading: isLoadingDetalle } = useFacturaCompra(
    facturaParaEditar?.id || 0,
    !!facturaParaEditar
  );

  const facturas = facturasData?.data || [];
  const pagination = facturasData?.pagination;

  // La factura completa para el wizard: solo se usa cuando ya se cargaron los detalles.
  // Si facturaDetalleResponse aún no llegó, es undefined → el wizard aún no se abre.
  const facturaCompletaParaEditar = facturaDetalleResponse?.data ?? null;

  const handleCrearFactura = () => {
    setFacturaParaEditar(null);
    setShowWizard(true);
  };

  /**
   * Construye el payload completo para crear o editar una factura,
   * incluyendo todos los campos de jerarquía/pack.
   */
  const construirPayload = (data: WizardFacturaData) => ({
    numero_factura: data.numero_factura,
    proveedor_id: data.proveedor_id,
    rut_proveedor: data.rut_proveedor,
    nombre_proveedor: data.nombre_proveedor,
    fecha_emision: data.fecha_emision,
    metodo_pago: data.metodo_pago,
    orden_compra_id: data.orden_compra_id,
    observaciones: data.observaciones,
    detalles: data.detalles.map((d) => ({
      ingrediente_id: d.ingrediente_id,
      nombre_ingrediente_temporal: d.nombre_ingrediente_temporal,
      marca: d.marca,
      categoria_nombre: d.categoria_nombre,
      categoria_id: d.categoria_id,
      cantidad: d.cantidad,
      unidad_medida: d.unidad_medida,
      precio_unitario: d.precio_unitario,
      subtotal: d.subtotal,
      observaciones: d.observaciones,
      // Campos de pack/jerarquía
      es_pack: d.es_pack,
      cantidad_packs: d.cantidad_packs,
      unidades_por_pack: d.unidades_por_pack,
      precio_pack: d.precio_pack,
      formato_compra: d.formato_compra,
      jerarquia_niveles: d.jerarquia_niveles,
      nivel1_cantidad: d.nivel1_cantidad,
      nivel1_nombre: d.nivel1_nombre,
      nivel1_unidades: d.nivel1_unidades,
      nivel2_nombre: d.nivel2_nombre,
      nivel2_cantidad_base: d.nivel2_cantidad_base,
      precio_nivel1: d.precio_nivel1,
      precio_nivel2: d.precio_nivel2,
      precio_base: d.precio_base,
      descripcion_jerarquia: d.descripcion_jerarquia,
    })),
  });

  const handleGuardarFactura = async (data: WizardFacturaData) => {
    try {
      await crearFacturaMutation.mutateAsync(construirPayload(data));
      setShowWizard(false);
    } catch (error) {
      console.error('Error al crear factura:', error);
      throw error;
    }
  };

  const handleActualizarFactura = async (data: WizardFacturaData) => {
    if (!facturaParaEditar) return;
    try {
      await editarFacturaMutation.mutateAsync({
        id: facturaParaEditar.id,
        data: construirPayload(data),
      });
      setShowWizard(false);
      setFacturaParaEditar(null);
    } catch (error) {
      console.error('Error al editar factura:', error);
      throw error;
    }
  };

  const handleVerDetalle = (id: number) => {
    setFacturaSeleccionada(id);
    setShowDetalle(true);
  };

  const handleCerrarDetalle = () => {
    setShowDetalle(false);
    setFacturaSeleccionada(null);
  };

  /**
   * Inicia el flujo de edición: guarda la factura seleccionada y cierra
   * el modal de detalle. El wizard se abre automáticamente cuando
   * facturaDetalleResponse complete la carga (ver useEffect abajo).
   */
  const handleEditarFactura = (factura: FacturaCompra) => {
    setFacturaParaEditar(factura);
    setShowDetalle(false);
    setFacturaSeleccionada(null);
    // No abrir showWizard aquí — se abre en el useEffect cuando los detalles están listos
  };

  // Abrir el wizard solo cuando los detalles completos de la factura hayan cargado.
  // Esto evita el reset doble (primero con detalles vacíos, luego con datos reales)
  // que causaba que los precios se mostraran incorrectos al abrir el modo edición.
  useEffect(() => {
    if (facturaParaEditar && facturaCompletaParaEditar && !isLoadingDetalle) {
      setShowWizard(true);
    }
  }, [facturaParaEditar, facturaCompletaParaEditar, isLoadingDetalle]);

  const handleAnularFactura = async (id: number) => {
    try {
      await anularFacturaMutation.mutateAsync(id);
    } catch (error) {
      console.error('Error al anular factura:', error);
      throw error;
    }
  };

  const estadoColor = (estado: string) => {
    switch (estado) {
      case 'borrador':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-200 dark:border-yellow-800';
      case 'registrada':
        return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-200 dark:border-green-800';
      case 'anulada':
        return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-200 dark:border-red-800';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-800">Error al cargar facturas</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Controles */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                placeholder="Buscar por numero de factura..."
                value={filtros.numero_factura || ''}
                onChange={(e) =>
                  setFiltros({ ...filtros, numero_factura: e.target.value })
                }
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                         focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <button
              onClick={() => setShowFiltros(!showFiltros)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                       hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2
                       text-gray-700 dark:text-gray-300"
            >
              <Filter className="w-4 h-4" />
              Filtros
            </button>
          </div>

          <button
            onClick={handleCrearFactura}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700
                     transition-colors flex items-center gap-2 shadow-sm"
          >
            <Plus className="w-5 h-5" />
            Nueva Factura
          </button>
        </div>

        {/* Filtros expandibles */}
        {showFiltros && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Fecha Desde
              </label>
              <input
                type="date"
                value={filtros.fecha_desde || ''}
                onChange={(e) =>
                  setFiltros({ ...filtros, fecha_desde: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Fecha Hasta
              </label>
              <input
                type="date"
                value={filtros.fecha_hasta || ''}
                onChange={(e) =>
                  setFiltros({ ...filtros, fecha_hasta: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Estado
              </label>
              <select
                value={filtros.estado || ''}
                onChange={(e) =>
                  setFiltros({
                    ...filtros,
                    estado: e.target.value as any,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="">Todos</option>
                <option value="borrador">Borrador</option>
                <option value="registrada">Registrada</option>
                <option value="anulada">Anulada</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Tabla de facturas */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Numero
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Fecha
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Proveedor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Items
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {facturas.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-12 text-center text-gray-500 dark:text-gray-400"
                  >
                    <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                    <p className="text-lg font-medium">
                      No hay facturas registradas
                    </p>
                    <p className="text-sm mt-1">
                      Comienza creando tu primera factura de compra
                    </p>
                  </td>
                </tr>
              ) : (
                facturas.map((factura) => (
                  <tr
                    key={factura.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <FileText className="w-5 h-5 text-gray-400 dark:text-gray-500 mr-2" />
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {factura.numero_factura}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                        <Calendar className="w-4 h-4 mr-1" />
                        {formatearFecha(factura.fecha_emision)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          {factura.nombre_proveedor}
                        </div>
                        <div className="text-gray-500 dark:text-gray-400 text-xs font-mono">
                          {factura.rut_proveedor
                            ? (() => {
                                try {
                                  return formatearRut(factura.rut_proveedor);
                                } catch {
                                  return factura.rut_proveedor;
                                }
                              })()
                            : '-'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {formatearMoneda(factura.total)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {factura.total_items || 0} items
                        {factura.ingredientes_nuevos && factura.ingredientes_nuevos > 0 ? (
                          <div className="text-xs text-green-600 dark:text-green-400">
                            +{factura.ingredientes_nuevos} nuevos
                          </div>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${estadoColor(
                          factura.estado
                        )}`}
                      >
                        {factura.estado.charAt(0).toUpperCase() +
                          factura.estado.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleVerDetalle(factura.id)}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                          title="Ver detalles"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                        {factura.estado !== 'anulada' && (
                          <button
                            onClick={() => handleEditarFactura(factura)}
                            className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                            title="Editar factura"
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                        )}
                        {factura.imagen_url && (
                          <a
                            href={factura.imagen_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400"
                            title="Ver imagen"
                          >
                            <Download className="w-5 h-5" />
                          </a>
                        )}
                        {factura.estado !== 'anulada' && (
                          <button
                            onClick={() => handleAnularFactura(factura.id)}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                            title="Anular factura"
                          >
                            <XCircle className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Paginacion */}
        {pagination && pagination.total > pagination.limit && (
          <div className="bg-gray-50 dark:bg-gray-700 px-6 py-3 border-t border-gray-200 dark:border-gray-600">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                Mostrando{' '}
                <span className="font-medium">{pagination.offset + 1}</span> a{' '}
                <span className="font-medium">
                  {Math.min(
                    pagination.offset + pagination.limit,
                    pagination.total
                  )}
                </span>{' '}
                de <span className="font-medium">{pagination.total}</span>{' '}
                facturas
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() =>
                    setFiltros({
                      ...filtros,
                      offset: Math.max(
                        0,
                        (filtros.offset || 0) - (filtros.limit || 50)
                      ),
                    })
                  }
                  disabled={(filtros.offset || 0) === 0}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                           hover:bg-gray-100 dark:hover:bg-gray-600
                           disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                <button
                  onClick={() =>
                    setFiltros({
                      ...filtros,
                      offset: (filtros.offset || 0) + (filtros.limit || 50),
                    })
                  }
                  disabled={
                    (filtros.offset || 0) + (filtros.limit || 50) >=
                    pagination.total
                  }
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                           hover:bg-gray-100 dark:hover:bg-gray-600
                           disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Siguiente
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Overlay de carga mientras se obtienen los detalles de la factura a editar */}
      {facturaParaEditar && isLoadingDetalle && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl px-8 py-6 flex items-center gap-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            <span className="text-gray-700 dark:text-gray-200 text-sm font-medium">
              Cargando factura...
            </span>
          </div>
        </div>
      )}

      {/* Wizard nuevo (creacion y edicion) */}
      <WizardFacturaCompraNew
        isOpen={showWizard}
        onClose={() => {
          setShowWizard(false);
          setFacturaParaEditar(null);
        }}
        onSubmit={handleGuardarFactura}
        initialData={facturaCompletaParaEditar || undefined}
        onUpdate={handleActualizarFactura}
      />

      {/* Modal de detalle */}
      {showDetalle && facturaSeleccionada && (
        <DetalleFacturaCompra
          isOpen={showDetalle}
          onClose={handleCerrarDetalle}
          facturaId={facturaSeleccionada}
          onEdit={handleEditarFactura}
          onAnular={async (id) => {
            await handleAnularFactura(id);
            handleCerrarDetalle();
          }}
        />
      )}
    </div>
  );
};

export default FacturasCompra;
